import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";
import { IsFilterable, Filter, FilterTypes, FilterConfig } from "@/lib/filterable";
import { FormConfig, IsFormable, Form } from "@/lib/formable";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { RulesEnum } from "@/lib/rules/rules.map";
import { $t } from "@/lib/utility/t";
import {
  MrfiktivAddressGen,
  MrfiktivAddressWithGeoGen,
  MrfiktivPartialAddressWithGeoViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";

@IsFormable
@IsFilterable
class AddressBase implements MrfiktivAddressGen {
  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.address",
    searchKeywords: ["objects.address.street", "common.nouns.address"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.address.street"
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    displayName: "objects.address.street",
    type: FilterTypes.STRING
  })
  street: string;

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.address",
    searchKeywords: ["objects.address.street", "common.nouns.address"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.address.street"
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    displayName: "objects.address.street",
    type: FilterTypes.STRING
  })
  line2?: string | null;

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.address",
    searchKeywords: ["objects.address.street", "common.nouns.address"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.address.street"
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    displayName: "objects.address.street",
    type: FilterTypes.STRING
  })
  line3?: string | null;

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.address",
    searchKeywords: ["objects.address.zip", "common.nouns.address"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.address.zip"
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    displayName: "objects.address.zip",
    type: FilterTypes.STRING
  })
  zip: string;

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.address",
    searchKeywords: ["objects.address.city", "common.nouns.address"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.address.city"
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    displayName: "objects.address.city",
    type: FilterTypes.STRING
  })
  city: string;

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.address",
    searchKeywords: ["objects.address.zip", "common.nouns.address"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.address.zip"
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    displayName: "objects.address.state",
    type: FilterTypes.STRING
  })
  state: string;

  /**
   * @inheritdoc
   */
  @FormConfig({
    category: "common.nouns.address",
    searchKeywords: ["objects.address.countryCode", "common.nouns.address"],
    type: DetailFormComponentsEnum.AUTO_COMPLETE,
    props: {
      label: "objects.address.countryCode",
      items: Object.values(CountryCodeEnum).map(v => {
        return {
          text: $t(`CountryCodeEnum.${v}`),
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  @FilterConfig({
    displayName: "objects.address.countryCode",
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(CountryCodeEnum).map(v => {
        return {
          text: `CountryCodeEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    }
  })
  countryCode?: string | undefined;

  constructor(address?: Partial<MrfiktivAddressGen> | null) {
    this.street = address?.street ?? "";
    this.zip = address?.zip ?? "";
    this.city = address?.city ?? "";
    this.state = address?.state ?? "";
    this.countryCode = address?.countryCode;
    this.line2 = address?.line2 ?? undefined;
    this.line3 = address?.line3 ?? undefined;
  }

  toString() {
    return this.street ? `${this.street}, ${this.zip} ${this.city}` : `${this.zip} ${this.city}`;
  }
}
export class AddressWithGeoBase extends AddressBase implements MrfiktivAddressWithGeoGen {
  geo: {
    lat: number;
    lng: number;
  };
  constructor(address?: Partial<MrfiktivAddressWithGeoGen> | MrfiktivPartialAddressWithGeoViewModelGen | null) {
    super(address);
    this.geo = address?.geo ?? { lat: 0, lng: 0 };
  }

  toString() {
    return super.toString();
  }
}
type IAddress = AddressBase;
type IAddressWithGeo = AddressWithGeoBase;
const Address = Form.createForClass(Filter.createForClass(AddressBase));
const AddressWithGeo = Filter.createForClass(AddressWithGeoBase);

export { Address, AddressWithGeo, IAddress, IAddressWithGeo };
